import { OHIFVersions } from '@dynamika-shared/types/OHIFVersions';
import { OHIFViewerModeType } from '@dynamika-shared/types/viewerMode';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectConfig } from '@restore/clinicalTrialContext/clinicalTrialContext.slice';
import type { RootState } from '@restore/store';
import type { Context } from '@dynamika-shared/types/clinicalTrialContext';

const toEnum = (
  version: string | undefined | null
): OHIFVersions | undefined => {
  const ver = version?.toLowerCase();
  if (ver === 'v3.9.0' || ver === 'v3_9_0') return OHIFVersions.v3_9_0;
  if (ver === 'v3.11.0' || ver === 'v3_11_0') return OHIFVersions.v3_11_0;
  return undefined;
};

// URL / localStorage override is honored on prod always; on localhost only
// when dev mode is explicitly enabled.
const isUrlStorageSourceAllowed = (): boolean => {
  const isLocalhost = window.location.hostname === 'localhost';
  return isLocalhost;
};

// Source 1: explicit dev/QA override via URL or localStorage.
// Mode-specific wins over global; URL wins over localStorage.
const versionFromUrlOrStorage = (
  mode: OHIFViewerModeType
): OHIFVersions | undefined => {
  if (!isUrlStorageSourceAllowed()) return undefined;
  const urlParams = new URLSearchParams(window.location.search);
  return toEnum(
    urlParams.get(`version_${mode.toLowerCase()}`) ||
      urlParams.get('version') ||
      localStorage.getItem(`OHIFVersion_${mode}`) ||
      localStorage.getItem('OHIFVersion')
  );
};

// Source 2: clinical trial context delivered by the backend.
const versionFromContext = (
  config: Context | undefined
): OHIFVersions | undefined => toEnum(config?.dicomViewerVersion);

// Persist a `?version=...` URL param into localStorage so reloads remember
// it. Pure side-effect, kept separate from version resolution.
const persistVersionUrlParam = (mode: OHIFViewerModeType): void => {
  if (!isUrlStorageSourceAllowed()) return;
  const urlParams = new URLSearchParams(window.location.search);
  const modeParam = urlParams.get(`version_${mode.toLowerCase()}`);
  if (modeParam) {
    localStorage.setItem(`OHIFVersion_${mode}`, modeParam);
    return;
  }
  const globalParam = urlParams.get('version');
  if (globalParam) {
    localStorage.setItem('OHIFVersion', globalParam);
  }
};

const selectCtxResolved = (state: RootState): boolean =>
  !!state.clinicalTrialContext?.data || !!state.clinicalTrialContext?.error;

export type UseOHIFVersionResult = {
  version: OHIFVersions | undefined;
  isReady: boolean;
};

export const useOHIFVersion = (
  mode: OHIFViewerModeType
): UseOHIFVersionResult => {
  useEffect(() => {
    persistVersionUrlParam(mode);
  }, [mode]);

  const config = useSelector(selectConfig);
  const ctxResolved = useSelector(selectCtxResolved);

  const version =
    versionFromUrlOrStorage(mode) ??
    versionFromContext(config) ??
    (ctxResolved ? OHIFVersions.v3_9_0 : undefined);

  return { version, isReady: version !== undefined };
};
